import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { taskActionErrorToast } from '../../../utils/taskActionToast.js';
import { formatTaskEntityType } from '../../../utils/textCase.js';
import { shortEventId } from '../../../utils/formatEventId.js';
import { CheckCircle2 } from 'lucide-react';

/**
 * Task decision (CT-5093): Resolve closes the task via POST /v1/task/resolve/
 * {task_uuid}. Confirm modal summarizes the assignment before closing.
 */
function TaskDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { completeTask, pendingDecisionIds } = useWorkQueueContext();
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;
  const [confirming, setConfirming] = useState(false);
  const detail = event.detail ?? {};

  const entityType = formatTaskEntityType(detail.entityType, t);
  const entityRef = detail.entityUuid ? shortEventId(detail.entityUuid) : null;

  const resolveRows = useMemo(
    () =>
      [
        { label: t('task.detailMessage'), value: event.situation ?? event.description },
        entityType ? { label: t('task.entityType'), value: entityType } : null,
        entityRef ? { label: t('task.entityRef'), value: entityRef } : null,
        event.owner?.name ? { label: t('task.assignee'), value: event.owner.name } : null,
      ].filter((row) => row?.value),
    [t, event, entityType, entityRef],
  );

  const handleClose = useCallback(() => setConfirming(false), []);

  const handleResolve = useCallback(async () => {
    if (isResolving) return;
    try {
      await completeTask(event.id);
      toast.success({ title: t('task.resolvedTitle'), message: t('task.resolvedMessage') });
      handleClose();
    } catch (err) {
      const { title, message } = taskActionErrorToast(t, err, 'resolve');
      toast.error({ title, message });
      throw err;
    }
  }, [completeTask, event.id, handleClose, isResolving, t, toast]);

  const primaryActions = useMemo(
    () => [
      {
        id: 'resolve',
        label: isResolving ? t('task.resolving') : t('task.resolve'),
        size: 'md',
        icon: CheckCircle2,
        disabled: isResolving,
        onClick: () => setConfirming(true),
      },
    ],
    [isResolving, t],
  );

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={isResolving ? [] : actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={confirming}
        onClose={handleClose}
        icon={<CheckCircle2 size={20} className="text-emerald-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('task.resolveTitle')}
        subtitle={t('task.resolveSubtitle')}
        summaryRows={resolveRows}
        confirmLabel={t('task.resolve')}
        onConfirm={handleResolve}
      />
    </>
  );
}

export default TaskDecision;
